import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Snackbar,
  Alert,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';

const AddShift = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.shiftData;

  const [formData, setFormData] = useState({
    shiftName: '',
    shiftType: '',
    shiftCode: '',
    startTime: '',
    endTime: '',
    duration: 'Based on Start Time & End Time',
    breakDuration: '',
    description: '',
    recurringShift: false,
    recurrencePattern: '',
    repeatEvery: '',
    startDate: '',
    endDate: '',
    daysOfWeek: {
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false
    }
  });

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (editData) {
      setFormData(f => ({
        ...f,
        shiftName: editData.shiftName || '',
        shiftCode: editData.shiftCode || '',
        startTime: editData.startTime || '',
        endTime: editData.endTime || '',
        breakTime: editData.breakTime || ''
      }));
    }
  }, [editData]);

  useEffect(() => {
    const { startTime, endTime } = formData;
    if (startTime && endTime) {
      // Calculate duration based on start and end time
      const start = new Date(`2024-01-01 ${startTime}`);
      const end = new Date(`2024-01-01 ${endTime}`);
      let diff = end - start;

      if (diff < 0) {
        diff = 24 * 60 * 60 * 1000 + diff; // Add 24 hours if end time is next day
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setFormData(prev => ({
        ...prev,
        duration: `${hours}h ${minutes}m`
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        duration: 'Based on Start Time & End Time'
      }));
    }
  }, [formData]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDayChange = (day) => (event) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: {
        ...prev.daysOfWeek,
        [day]: event.target.checked
      }
    }));
  };

  const handleCancel = () => {
    navigate('/shift-schedule');
  };

  const handleSave = () => {
    showNotification(editData ? 'Shift updated successfully' : 'Shift created successfully', 'success');
    setTimeout(() => {
      navigate('/shift-schedule');
    }, 1500);
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  return (
    <Box sx={{ width: '100%', marginTop: 0 }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#F5F5F5',
          padding: '20px 24px',
          marginBottom: 3,
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#030007',
            fontSize: '1.1rem',
          }}
        >
          {editData ? 'Edit Shift' : 'Add Shift'}
        </Typography>
        <IconButton onClick={handleCancel}>
          <CloseIcon sx={{ color: '#4F4937' }} />
        </IconButton>
      </Box>

      <Card
        sx={{
          padding: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderRadius: 1,
        }}
      >
        <Grid container spacing={3}>
          {/* Shift Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Shift Name"
              placeholder="Enter here"
              value={formData.shiftName}
              onChange={handleInputChange('shiftName')}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Grid>

          {/* Shift Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Shift Type</InputLabel>
              <Select
                value={formData.shiftType}
                onChange={handleInputChange('shiftType')}
                label="Shift Type"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                }}
              >
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="overtime">Overtime</MenuItem>
                <MenuItem value="weekend">Weekend</MenuItem>
                <MenuItem value="holiday">Holiday</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Shift Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Shift Code"
              placeholder="Enter here"
              value={formData.shiftCode}
              onChange={handleInputChange('shiftCode')}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Grid>

          {/* Start Time */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Time"
              type="time"
              value={formData.startTime}
              onChange={handleInputChange('startTime')}
              InputProps={{
                startAdornment: <AccessTimeIcon sx={{ marginRight: 1, color: '#999' }} />,
              }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Grid>

          {/* End Time */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Time"
              type="time"
              value={formData.endTime}
              onChange={handleInputChange('endTime')}
              InputProps={{
                startAdornment: <AccessTimeIcon sx={{ marginRight: 1, color: '#999' }} />,
              }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Grid>

          {/* Duration */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Duration"
              value={formData.duration}
              disabled
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: '#F5F5F5',
                },
              }}
            />
          </Grid>

          {/* Break Duration */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Break Duration</InputLabel>
              <Select
                value={formData.breakDuration}
                onChange={handleInputChange('breakDuration')}
                label="Break Duration"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                  },
                }}
              >
                <MenuItem value="15 min">15 min</MenuItem>
                <MenuItem value="30 min">30 min</MenuItem>
                <MenuItem value="45 min">45 min</MenuItem>
                <MenuItem value="1 hr">1 hr</MenuItem>
                <MenuItem value="1.5 hr">1.5 hr</MenuItem>
                <MenuItem value="2 hr">2 hr</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              placeholder="Enter here"
              value={formData.description}
              onChange={handleInputChange('description')}
              multiline
              rows={3}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Grid>

          {/* Recurring Shift */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.recurringShift}
                  onChange={handleInputChange('recurringShift')}
                  sx={{ color: '#7522D1' }}
                />
              }
              label="Recurring Shift"
            />
          </Grid>

          {/* Recurrence Pattern - Conditional */}
          {formData.recurringShift && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Recurrence Pattern</InputLabel>
                  <Select
                    value={formData.recurrencePattern}
                    onChange={handleInputChange('recurrencePattern')}
                    label="Recurrence Pattern"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Repeat Every</InputLabel>
                  <Select
                    value={formData.repeatEvery}
                    onChange={handleInputChange('repeatEvery')}
                    label="Repeat Every"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  >
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3">3</MenuItem>
                    <MenuItem value="4">4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={handleInputChange('startDate')}
                  InputProps={{
                    startAdornment: <EventIcon sx={{ marginRight: 1, color: '#999' }} />,
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={handleInputChange('endDate')}
                  InputProps={{
                    startAdornment: <EventIcon sx={{ marginRight: 1, color: '#999' }} />,
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                  }}
                />
              </Grid>

              {/* Days of Week */}
              <Grid item xs={12}>
                <Typography sx={{ fontWeight: 600, marginBottom: 2, color: '#030007' }}>
                  Days of Week
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    { key: 'mon', label: 'Mon' },
                    { key: 'tue', label: 'Tue' },
                    { key: 'wed', label: 'Wed' },
                    { key: 'thu', label: 'Thu' },
                    { key: 'fri', label: 'Fri' },
                    { key: 'sat', label: 'Sat' },
                    { key: 'sun', label: 'Sun' }
                  ].map((day) => (
                    <FormControlLabel
                      key={day.key}
                      control={
                        <Checkbox
                          checked={formData.daysOfWeek[day.key]}
                          onChange={handleDayChange(day.key)}
                          sx={{ color: '#7522D1' }}
                        />
                      }
                      label={day.label}
                    />
                  ))}
                </Box>
              </Grid>
            </>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  borderColor: '#E8E8E8',
                  color: '#030007',
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingX: 3,
                  paddingY: 1,
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: '#7522D1',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  backgroundColor: '#7522D1',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingX: 3,
                  paddingY: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#5E1BA8',
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddShift;
